/**
 * How an insights answer should be LABELLED in the assistant's session log.
 *
 * <p>xt run 20260913-214629 asked "How much did we pay the supplier Hestia's Hearth this quarter?"
 * The assistant resolved it to the Payments Activity source with no filters at all, rendered every
 * row, and logged the turn as <strong>Answered</strong> — while its own answer text admitted it
 * could not filter by the named party. A log that says "Answered" for an answer to a different,
 * broader question is the one place a user cannot check: the caveat sits in the panel they have
 * already scrolled past, and the durable record says the question was met.
 *
 * <p>The detection is NOT a second heuristic: it reuses {@link unappliedNamedEntityCaveat} from
 * PR #752 verbatim, so the log state and the panel's caveat can never disagree about the same
 * answer. This module only decides which existing honesty state the turn gets.
 */
import { unappliedNamedEntityCaveat } from '../features/insights-unapplied-constraint';
import type { ResolveIntentResponse } from './assist.model';
import type { AssistTurnState } from './assist-thread.service';

export interface InsightsTurnOutcome {
  /** 'answered' when the answer met the question; 'partial' when a named constraint was dropped. */
  readonly state: Extract<AssistTurnState, 'answered' | 'partial'>;
  /** The session-log caption: the honest caveat when partial, the neutral label otherwise. */
  readonly detail: string;
}

const ANSWERED: InsightsTurnOutcome = { state: 'answered', detail: 'Insights answer' };

function stringParam(response: ResolveIntentResponse, key: string): string | null {
  const value = response.params?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function answerBody(response: ResolveIntentResponse): { data?: unknown } | null {
  const answer = response.insightsAnswer?.answer;
  return answer && typeof answer === 'object' ? (answer as { data?: unknown }) : null;
}

/**
 * Classify a resolved ASK_INSIGHTS response for the session log.
 *
 * Returns 'partial' — with the reason the user can read — when the query named a specific party
 * that the resolved source could not filter by; 'answered' otherwise.
 */
export function insightsTurnOutcome(response: ResolveIntentResponse | null): InsightsTurnOutcome {
  if (!response?.insightsAnswer) return ANSWERED;
  const caveat = unappliedNamedEntityCaveat(
    stringParam(response, 'query'),
    response.insightsAnswer.intent ?? null,
    answerBody(response)?.data ?? null,
  );
  return caveat ? { state: 'partial', detail: caveat } : ANSWERED;
}
